import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, TOKEN_STORAGE_KEY } from '../api/client';
import type { LoginResponse, MeResponse, RegisterRequest, RegisterResponse } from '../types/api';

export type LoginResult =
  | { status: 'authenticated'; user: MeResponse }
  | { status: 'two_factor_required'; challengeToken: string };

interface AuthContextValue {
  user: MeResponse | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<LoginResult>;
  verifyTwoFactorLogin: (challengeToken: string, code: string) => Promise<MeResponse>;
  register: (email: string, password: string, displayName: string, inviteCode?: string) => Promise<RegisterResponse>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<MeResponse | null>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function readStoredToken(): string | null {
  if (typeof window === 'undefined') return null;
  return window.localStorage.getItem(TOKEN_STORAGE_KEY);
}

function writeStoredToken(value: string | null): void {
  if (typeof window === 'undefined') return;
  if (value == null) {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  } else {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, value);
  }
}

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [token, setToken] = useState<string | null>(() => readStoredToken());
  const [user, setUser] = useState<MeResponse | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const refreshUser = useCallback(async (): Promise<MeResponse | null> => {
    const current = readStoredToken();
    if (!current) {
      setUser(null);
      return null;
    }
    try {
      const resp = await api.get<MeResponse>('/api/auth/me');
      setUser(resp.data);
      return resp.data;
    } catch {
      writeStoredToken(null);
      setToken(null);
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      await refreshUser();
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshUser]);

  const finishLogin = useCallback(async (accessToken: string): Promise<MeResponse> => {
    writeStoredToken(accessToken);
    setToken(accessToken);
    const me = await api.get<MeResponse>('/api/auth/me');
    setUser(me.data);
    return me.data;
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<LoginResult> => {
    const resp = await api.post<LoginResponse>('/api/auth/login', { email, password });
    if (resp.data.requiresTwoFactor) {
      if (!resp.data.twoFactorChallengeToken) {
        throw new Error('Two-factor challenge was not returned.');
      }
      return { status: 'two_factor_required', challengeToken: resp.data.twoFactorChallengeToken };
    }
    if (!resp.data.accessToken) {
      throw new Error('Access token was not returned.');
    }
    const userData = await finishLogin(resp.data.accessToken);
    return { status: 'authenticated', user: userData };
  }, [finishLogin]);

  const verifyTwoFactorLogin = useCallback(async (challengeToken: string, code: string): Promise<MeResponse> => {
    const resp = await api.post<LoginResponse>('/api/auth/2fa/login', { challengeToken, code });
    if (!resp.data.accessToken) {
      throw new Error('Access token was not returned.');
    }
    return finishLogin(resp.data.accessToken);
  }, [finishLogin]);

  const register = useCallback(async (
    email: string,
    password: string,
    displayName: string,
    inviteCode?: string,
  ): Promise<RegisterResponse> => {
    const payload: RegisterRequest = { email, password, displayName };
    const trimmedInviteCode = inviteCode?.trim();
    if (trimmedInviteCode) {
      payload.inviteCode = trimmedInviteCode;
    }
    const resp = await api.post<RegisterResponse>('/api/auth/register', payload);
    return resp.data;
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      await api.post('/api/auth/logout');
    } catch {
      // ignore network/401; we always clear locally
    }
    writeStoredToken(null);
    setToken(null);
    setUser(null);
  }, []);

  const value = useMemo<AuthContextValue>(() => ({
    user,
    token,
    loading,
    login,
    verifyTwoFactorLogin,
    register,
    logout,
    refreshUser,
  }), [user, token, loading, login, verifyTwoFactorLogin, register, logout, refreshUser]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}
